import { camelCase, get, filter, isEmpty } from 'lodash';
import { isMobile } from 'mobile-device-detect';
import { BREAKPOINTS } from './constant';
import axios from '@/config/axois.config';
import {
  FightStatus,
  IBetHistoryData,
  IFight,
  ITicket,
  ITicketTotal,
  KeyDownKey,
  NumericInputType,
  PopupPage,
} from './index.model';

export const getData = async (url: string, token?: string, params?: any) => {
  const headers: any = {};
  if (token) {
    headers.authorization = `Token ${token}`;
  }
  const response = await axios.get(url, { params, headers });
  return response.data;
};

export const postData = async (url: string, token?: string, params?: any, captureServerTime?: boolean) => {
  const headers: any = { params };
  if (token) {
    headers.authorization = `Token ${token}`;
  }
  const response = await axios.post(url, params, { headers });

  if (captureServerTime) {
    // Calculate server time difference from Date header
    const serverDateHeader = response.headers?.date;
    let serverTimeDiff = 0;
    if (serverDateHeader) {
      const serverTime = new Date(serverDateHeader).getTime();
      const clientTime = Date.now();
      serverTimeDiff = serverTime - clientTime;
    }
    return {
      ...response.data,
      serverTimeDiff,
    };
  }

  return response.data;
};

/*
 * The bet endpoint reports a rejected bet as `{ code, message, partial }`
 * (e.g. code 12 "invalid bet selection"); an accepted one is the list of
 * tickets. The HTTP status cannot be relied on to tell them apart -- the axios
 * layer resolves any 200 -- so a rejection body is turned into the same
 * rejection a 4xx gives ({ data }), and nothing downstream (ticket refresh,
 * "accepted" feedback, clearing the slip) runs for a bet that was not placed.
 */
export const assertBetAccepted = (response: any) => {
  const isRejection =
    !!response &&
    !Array.isArray(response) &&
    typeof response === 'object' &&
    typeof response.code === 'number' &&
    response.code !== 0 &&
    'message' in response;
  if (isRejection) throw { data: response };
  return response;
};

export const patchData = async (url: string, token?: string, params?: any) => {
  const headers: any = { params };
  if (token) {
    headers.authorization = `Token ${token}`;
  }
  const response = await axios.patch(url, params, { headers });
  return response.data;
};

const isObject = (o: any): boolean => o === Object(o) && !Array.isArray(o) && typeof o !== 'function';

export const keysToCamel = (o: any): any => {
  if (isObject(o)) {
    const n = {} as { [key: string]: any };

    Object.keys(o).forEach((k) => {
      n[camelCase(k)] = keysToCamel(o[k]);
    });

    return n;
  } else if (Array.isArray(o)) {
    return o.map((i) => {
      return keysToCamel(i);
    });
  }

  return o;
};

export const getTimeDiffCorrection = ({
  serverTime,
  startTime,
}: {
  serverTime: string | number;
  startTime: number;
}): number => {
  const endTime = window.performance.now();
  return mathFloor(new Date(serverTime).getTime() + (endTime - startTime) / 2 - new Date().getTime());
};

export const mapWSRespToConfig = (config: any) => {
  return {
    eventId: config.currentEvent.id,
    eventTime: config.currentEvent?.startDatetime,
    betChips: config.betChips,
    soundSettings: {
      gameSound: config.gameSound * 100,
      effectSound: config.effectsSound * 100,
    },
    resultAnimation: config.resultAnimation,
  };
};

export const makeArray = (width: number, length: number) => {
  const arr = new Array(length);
  let i;
  let l;
  for (i = 0, l = length; i < l; i++) {
    arr[i] = new Array(width).fill(undefined);
  }
  return arr;
};

export const getRatio = (screenWidth: number, maxLimit: number, defaultNum: number): number => {
  if (!screenWidth && !maxLimit && !defaultNum) {
    return 0;
  }
  return (screenWidth / maxLimit) * defaultNum;
};

export const hexToRgb = (hex: string) => {
  const hexRes = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return hexRes
    ? {
        r: parseInt(hexRes[1], 16),
        g: parseInt(hexRes[2], 16),
        b: parseInt(hexRes[3], 16),
      }
    : null;
};

export const getImgUrl = (url: PopupPage) => {
  if (url === PopupPage.jackpotHelp || url === PopupPage.jackpotPhysicalHelp)
    return require('@/assets/images/help.webp');
  return require('@/assets/images/' + url.toLowerCase() + '.webp');
};

const getExchangeRate = () => {
  const xrate = getItemFromStore('general.general.member.exchangeRate');
  return xrate ? Number(xrate) : 1;
};

export const formatAmountWithKM = (value: number, floatNum: number = 2): string => {
  if (!value || typeof value !== 'number') {
    return '0.00';
  }

  let symbol = '';
  const xrate = getExchangeRate();

  if (xrate > 500) {
    if (value >= 1e6) {
      value = value / 1e6;
      symbol = 'M';
    } else if (value >= 1e3) {
      value = value / 1e3;
      symbol = 'K';
    }

    if (Number.isInteger(value)) {
      floatNum = 0;
    }
  }

  const formattedValue = value.toFixed(floatNum);
  const resNum = formattedValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  return `${resNum}${symbol}`;
};

const strip = (num: number): number => {
  return parseFloat(num.toPrecision(12));
};

export const mathFloor = (num: number) => {
  return Math.floor(strip(num));
};

export const mathCeil = (num: number) => {
  return Math.ceil(strip(num));
};

export const mathRound = (num: number) => {
  return Math.round(strip(num));
};

export const formatAmount = (value: number, floatNum: number = 2): string => {
  if (!value) {
    return '0.00';
  }
  const val: any = (mathFloor((value / 1) * 100) / 100).toFixed(floatNum).replace('.', '.');
  const resNum = val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return resNum;
};

/*
 * A stake for the bet columns: no decimals when it is a whole amount
 * (1,000.00 -> 1,000) to save room; a fractional one keeps its two.
 */
export const formatBetAmount = (value: number | string): string => {
  const amount = Number(value) || 0;
  return Number.isInteger(amount) ? formatAmount(amount, 0).replace(/^0\.00$/, '0') : formatAmount(amount, 2);
};

export const formatAmountFixed = (value: number | string, floatNum = 2): string => {
  const [i, d] = String(value ?? 0).split('.');
  const int = i.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return d ? `${int}.${d}` : `${int}.${'0'.repeat(floatNum)}`;
};

export const getItemFromStore = (path: string): any => {
  const storeData = sessionStorage.getItem('vuex');
  if (!storeData) {
    return;
  }
  return get(JSON.parse(storeData), path);
};

export const isIframeReload = (url: string) => {
  const isInIframe = parent !== window;
  return isInIframe && url === document.location.href;
};

export const viewPortWidth = () => Math.min(document.documentElement.clientWidth, window.innerWidth || 0);
export const viewPortHeight = () => Math.min(document.documentElement.clientHeight, window.innerHeight || 0);

export const capitalize = (str: string): string => {
  if (!str) return '';
  return str
    .split(' ')
    .map((s) => {
      return s[0].toUpperCase() + s.slice(1);
    })
    .join(' ');
};

export const capitalizeFirstLetter = (s: string): string => s.toLowerCase()[0].toUpperCase() + s.toLowerCase().slice(1);

export const isLandscape = () => {
  const width = viewPortWidth();
  const height = viewPortHeight();
  if (isMobile && Math.abs(window.orientation as number) === 0) {
    return false;
  }

  if (isMobile && width > BREAKPOINTS.ipad && height < BREAKPOINTS.mobile) {
    return true;
  }

  return isMobile && Math.abs(window.orientation as number) === 90 && !document.fullscreenElement;
};

export const twoWeeksRange = () => {
  const curDate = new Date();
  const dateList = [curDate.getTime()];

  for (let i = 1; i < 20; i++) {
    const tempDate = curDate.setDate(curDate.getDate() - 1);
    dateList.push(tempDate);
  }

  return dateList;
};

export const fightsListToChartData = (fights: IFight[]) => {
  const mapArr = {
    banker: 0,
    player: 1,
    draw: 2,
    cancel: 3,
  };
  if (!fights || isEmpty(fights)) return;
  const chartArr: number[] = [];
  fights.reverse().forEach((f: IFight) => {
    if (f.result) {
      chartArr.push(mapArr[f.result as keyof typeof mapArr]);
    }
  });

  return chartArr;
};

export const fightsListToFightData = (fights: IFight[]) => {
  const mapArr = {
    banker: 0,
    player: 1,
    draw: 2,
    cancel: 3,
  };
  if (!fights || isEmpty(fights)) return;
  const chartArr: { fight: number; result: number }[] = [];
  fights.forEach((f: IFight) => {
    if (f.result) {
      chartArr.push({
        fight: f.fightNumber,
        result: mapArr[f.result as keyof typeof mapArr],
      });
    }
  });

  return chartArr;
};

export const generateStatementData = (
  ticketTotal: ITicketTotal[],
  moment: any,
  timezone: string
): { data: IBetHistoryData[]; total: number } => {
  let total = 0;
  const statementData = ticketTotal.map((val: ITicketTotal) => {
    total += Number(val.totalAmount);
    return {
      date: moment(val.date).tz(timezone).format('DD/MM/YYYY'),
      dateVal: val.date,
      wl: val.winLossAmount,
      jackpot: val.jackpotAmount,
      ante: val.anteAmount,
      commission: val.commissionAmount,
      total: val.totalAmount,
      type: 'general',
      txn: val.ticketCount,
    };
  });
  return { data: statementData, total };
};
export const generateTicketData = (
  ticketListing: ITicket[],
  ticketTotal: ITicketTotal[],
  moment: any,
  timezone: string,
  isCreditUser: boolean
): IBetHistoryData[] => {
  let finalResult: IBetHistoryData[] = [];
  for (const [j, val] of ticketTotal.entries()) {
    const dayBets = filter(ticketListing, (ticket: ITicket) =>
      datesAreOnSameDay(val.date, ticket.createdDateTime, moment, timezone, isCreditUser)
    ) as ITicket[];

    if (dayBets && !isEmpty(dayBets)) {
      finalResult.push({
        date: moment(new Date(val.date)).tz(timezone).format('DD/MM/YYYY'),
        dateVal: val.date,
        wl: val.winLossAmount,
        earnings: val.earnings,
        jackpot: val.jackpotAmount,
        ante: val.anteAmount,
        commission: val.commissionAmount,
        total: val.totalAmount,
        type: 'general',
      });
      const convertDayBets = dayBets.map((t: ITicket) => ({
        // Guarded: a ticket can come without `fight.match_day`, and one bad row
        // must not take the whole list down.
        id: `${(t.fight && t.fight.matchDay && t.fight.matchDay.arena && t.fight.matchDay.arena.code) || ''}#${
          (t.fight && t.fight.fightNumber) || ''
        }`,
        bet: t.amount,
        date: moment(t.createdDateTime).tz(timezone).format('HH:mm:ss'),
        wl: t.winLossAmount,
        earnings: t.earnings,
        tax: t.taxAmount,
        type: 'detail',
        selection: t.selection,
        odds: t.odds,
        raw: t,
      }));
      finalResult = [...finalResult, ...convertDayBets];
    }
  }
  return finalResult;
};

export const datesAreOnSameDay = (
  firstDate: string,
  secondDate: string,
  moment: any,
  timezone: string,
  isCreditUser: boolean
) => {
  firstDate = firstDate + ' 12:00:00';

  const baseTz = isCreditUser ? 'Africa/Juba' : timezone;
  const first = moment.tz(firstDate, baseTz);
  const second = moment.tz(secondDate, baseTz);
  return second.isBetween(first.clone().startOf('day'), first.clone().endOf('day'), undefined, '[]');
};
// export const getCurrencySymbol = (currencyDisplayCode: string) => {
//   switch (currencyDisplayCode.toLowerCase()) {
//     case 'usd':
//       return '$';
//     case 'rmb':
//       return '¥';
//     case 'vnd':
//       return '₫';
//     case 'myr':
//       return 'RM';
//     case 'idr':
//       return 'Rp';
//     case 'bnd':
//     case 'sgd':
//       return '$';
//     case 'php':
//       return '₱';
//     case 'thb':
//       return '฿';
//     case 'cny':
//       return '¥';
//     case 'brl':
//       return 'R$';
//     default:
//       return '$';
//   }
// };

// Function to detect if prod=true in route query
export const isProdMode = (): boolean => {
  // Get the current query parameters
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const prodParam = urlParams.get('prod');

  // Check if prod=true in URL
  return prodParam === 'true';
};

export const isProduction = (): boolean =>
  isProdMode() || process.env.NODE_ENV === 'production';

export const getBaseUrl = () => {
  const production = isProduction();
  const hostName = window.location.hostname;

  const isIPAddress = /^(\d{1,3}\.){3}\d{1,3}$/.test(hostName);
  if (isIPAddress || hostName === 'localhost') {
    return 'dg8-api.com';
  }

  if (production) {
    if (hostName.startsWith('www.')) {
      return hostName.slice(4);
    }
  } else {
    const hostNameArr = hostName.split('.');
    if (hostName.includes('gc-dev') || hostName === 'localhost') {
      return 'dg8-api.com';
    }

    if (hostNameArr.length >= 2) {
      if (hostNameArr.length === 2) return hostName;
      return `${hostNameArr[hostNameArr.length - 2]}.${hostNameArr[hostNameArr.length - 1]}`;
    }
  }

  return 'dg8-api.com';
};

const DEVICE_ID_STORAGE_KEY = 'gc_device_id';

const generateDeviceId = (): string => {
  const cryptoApi = window.crypto as any;
  if (cryptoApi && typeof cryptoApi.randomUUID === 'function') {
    return cryptoApi.randomUUID();
  }

  return `ga28-${Date.now()}-${Math.random().toString(16).slice(2)}-${Math.random().toString(16).slice(2)}`;
};

export const getDeviceId = (): string => {
  try {
    let deviceId = window.localStorage.getItem(DEVICE_ID_STORAGE_KEY);
    if (!deviceId) {
      deviceId = generateDeviceId();
      window.localStorage.setItem(DEVICE_ID_STORAGE_KEY, deviceId);
    }
    return deviceId;
  } catch (e) {
    return '';
  }
};

export const sha256 = async (str: string): Promise<string> => {
  try {
    if (!window.crypto || !window.crypto.subtle) return '';
    const buf = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(str));
    return Array.from(new Uint8Array(buf))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch (e) {
    return '';
  }
};

const getDeviceFingerprintSource = (): string => {
  const nav = window.navigator as any;
  const screenInfo = window.screen;

  const fingerprintPayload = {
    userAgent: nav.userAgent || '',
    languages: Array.isArray(nav.languages) && nav.languages.length ? nav.languages : [nav.language || ''],
    platform: nav.platform || '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
    screen: {
      width: screenInfo.width || 0,
      height: screenInfo.height || 0,
      colorDepth: screenInfo.colorDepth || 0,
      pixelDepth: screenInfo.pixelDepth || 0,
    },
    deviceMemory: nav.deviceMemory || 0,
    maxTouchPoints: nav.maxTouchPoints || 0,
    hardwareConcurrency: nav.hardwareConcurrency || 0,
  };

  return JSON.stringify(fingerprintPayload);
};

export const getDeviceFingerprint = async (): Promise<string> => {
  return sha256(getDeviceFingerprintSource());
};

export const blockNonDigit = ($event: any, type: NumericInputType = NumericInputType.float) => {
  const { key, target } = $event;
  let controlKeys = [KeyDownKey.backspace, KeyDownKey.delete, KeyDownKey.arrowLeft, KeyDownKey.arrowRight];

  if (type === NumericInputType.float) {
    controlKeys = [...controlKeys, KeyDownKey.dot];
  }

  if (type === NumericInputType.decimal) {
    controlKeys = [...controlKeys, KeyDownKey.dot, KeyDownKey.comma];
    // handle for ios number pad
    if (key === ',') {
      $event.preventDefault();
      const cursorPosition = target.selectionStart;
      const value = target.value;
      target.value = value.slice(0, cursorPosition) + '.' + value.slice(cursorPosition);
      target.selectionStart = target.selectionEnd = cursorPosition + 1;
      const inputEvent = new Event('input', { bubbles: true });
      target.dispatchEvent(inputEvent);
    }
  }
  if (key !== KeyDownKey.space && ((key >= '0' && key <= '9') || controlKeys.includes(key))) {
    return;
  } else {
    $event.preventDefault();
  }
};

export const getFightDataFromBinary = (fightData: any) => {
  const parseTs = (ts?: number) => (ts ? new Date(ts * 1000).toISOString().slice(0, 19) : undefined);

  const map = {
    confirmedTs: (v: number) => ({ confirmed_date_time: parseTs(v) }),
    fightNumber: (v: string | number) => ({ fight_number: v }),
    id: (v: any) => ({ id: v }),
    fightId: (v: any) => ({ id: v }),
    isBetsPaused: (v: boolean) => ({ is_bets_paused: v }),
    replayUrl: (v: string) => ({ replay_url: v }),
    result: (v: string) => ({ result: v }),
    startedTs: (v: number) => ({ started_date_time: parseTs(v) }),
    status: (v: string | number) => ({
      status: typeof v === 'number' ? FightStatus[v] : v,
    }),
  };

  const updated: any = {};

  Object.entries(map).forEach(([key, fn]) => {
    if (fightData[key] !== undefined && fightData[key] !== null) Object.assign(updated, fn(fightData[key]));
  });

  if (fightData.matchDayId || fightData.arenaId || fightData.isReverse !== undefined) {
    updated.match_day = {
      ...(fightData.matchDayId && { id: fightData.matchDayId }),
      ...(fightData.arenaId && { arena: { id: fightData.arenaId } }),
      ...(typeof fightData.isReverse === 'boolean' && { is_reverse: fightData.isReverse }),
      ...(typeof fightData.hasDrawSelection === 'boolean' && { has_draw_selection: fightData.hasDrawSelection }),
    };
  }

  const makeSelection = (name: string, label: string) => {
    return {
      display_name: fightData[`${label}DisplayName`] ?? undefined,
      name,
      odds: fightData[`${label}Odds`],
      total_bet_amount: fightData[`${label}TotalAmount`] ?? fightData[`${label}Amount`],
      total_bet_count: fightData[`${label}TotalBetCount`] ?? fightData[`${label}Count`],
    };
  };

  const selections = [
    makeSelection('banker', 'banker'),
    makeSelection('player', 'player'),
    makeSelection('draw', 'draw'),
  ].filter(Boolean);

  if (selections.length) updated.selections = selections;

  return updated;
};

export const getFightResultFromBinary = (fights: any[]) => {
  return fights.map((fight) => {
    return {
      id: fight.id,
      fightNumber: fight.fightNumber,
      confirmedDateTime: new Date(fight.confirmedDateTimeTs * 1000).toISOString().slice(0, 19),
      result: fight.result,
      status: fight.status,
      selection: [
        {
          displayName: fight.bankerDisplayName,
          name: 'banker',
          odds: fight.bankerOdds,
        },
        {
          displayName: fight.drawDisplayName,
          name: 'draw',
          odds: fight.drawOdds,
        },
        {
          displayName: fight.playerDisplayName,
          name: 'player',
          odds: fight.playerOdds,
        },
      ],
    };
  });
};

export const normalizeStreamUrl = (url: string): string => {
  // Simply return the URL as-is - all platforms now use the same stream URL
  // Previously iOS was downgraded from ll-hls-cmaf to hls-cmaf, causing 2-10s latency
  return url || '';
};

export type ArenaStreamPlatform = 'windows' | 'ios' | 'android' | 'linux' | 'others';
export type ArenaStreamDrmType = 'fairplay' | 'widevine';
export type ArenaStreamDrmCode = 'fp' | 'wv';

export const getArenaStreamPlatform = (): ArenaStreamPlatform => {
  if (typeof window === 'undefined') return 'others';

  const ua = window.navigator.userAgent || '';
  const platform = window.navigator.platform || '';

  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  if (/Win/i.test(platform) || /Windows/i.test(ua)) return 'windows';
  if (/Linux/i.test(platform) || /Linux/i.test(ua)) return 'linux';

  return 'others';
};

export const getArenaStreamDrmType = (): ArenaStreamDrmType => {
  if (typeof navigator === 'undefined') return 'widevine';
  const ua = navigator.userAgent || '';
  // const platform = navigator.platform || '';
  const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
  const isIOSFamily =
    /iPad|iPhone|iPod/.test(ua) || (ua.includes('Mac') && typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 1);
  // const isMacOSDesktop = /Mac/i.test(platform) || /Macintosh|Mac OS X/i.test(ua);
  return isSafari || isIOSFamily ? 'fairplay' : 'widevine';
};

export const getArenaStreamDrmCode = (): ArenaStreamDrmCode => {
  return getArenaStreamDrmType() === 'fairplay' ? 'fp' : 'wv';
};

export const deepMerge = (target: any, source: any) => {
  for (const key in source) {
    const val = source[key];

    if (val === undefined) continue;

    if (Array.isArray(val)) {
      target[key] = target[key] || [];

      // Special handling for selections array - merge by name instead of index
      if (key === 'selections' && val.length > 0 && val[0]?.name) {
        const targetMap = new Map(target[key].map((item: any) => [item.name, item]));
        target[key] = val.map((item: any) => {
          if (item === undefined) return item;
          if (typeof item === 'object' && !Array.isArray(item) && item.name) {
            const existing = targetMap.get(item.name);
            return existing ? deepMerge({ ...(existing as Record<string, any>) }, item) : item;
          }
          return item;
        });
        continue;
      }

      target[key] = val.map((item, idx) => {
        if (item === undefined) return target[key][idx];
        if (typeof item === 'object' && !Array.isArray(item)) {
          return deepMerge({ ...target[key][idx] }, item);
        }
        return item;
      });

      continue;
    }

    if (val !== null && typeof val === 'object') {
      target[key] = deepMerge(target[key] || {}, val);
      continue;
    }

    target[key] = val;
  }

  return target;
};

export const formatLargeAmount = (value: number, floatNum: number = 3): string => {
  if (!value) {
    return '0.00';
  }
  const val: any = (value / 1_000_000).toFixed(floatNum).replace('.', '.');
  const resNum = val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${resNum}M`;
};
